import React, { useEffect, useState } from 'react';
import styles from './SchedulingFlow.module.css';
import AxisLogo from './SVGs/AxisLogo';

interface TherapyPreferenceStepProps {
  onProceed: () => void;
}

const REFERRAL_URL = 'https://go.axismh.com/axistherapyreferralrequests';
const NAV_PHONE = '(720) 400-7025';
const ORGANIZATION_NAME = 'Axis Integrated Mental Health';
const AXIS_WEBSITE = 'https://www.axismh.com/';

const TherapyPreferenceStep: React.FC<TherapyPreferenceStepProps> = ({ onProceed }) => {
  const NAV_LINKS = [
    { label: 'HOME', action: 'home' },
  ];
  const NAV_LINKS_MOBILE = [
    { label: 'HOME', action: 'home' },
  ];

  const [isMobile, setIsMobile] = useState(false);
  const [selectedPath, setSelectedPath] = useState<'therapy' | 'other' | null>(null);
  const [referralOpened, setReferralOpened] = useState(false);
  const [declinedReferral, setDeclinedReferral] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 700);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const currentNavLinks = isMobile ? NAV_LINKS_MOBILE : NAV_LINKS;

  const handleNavClick = (action: string) => {
    if (action === 'home') {
      window.location.reload();
    }
  };

  const handleSelectTherapy = () => {
    setSelectedPath('therapy');
    setReferralOpened(false);
    setDeclinedReferral(false);
  };

  const handleReferralRequest = () => {
    window.open(REFERRAL_URL, '_blank', 'noopener');
    setReferralOpened(true);
    setDeclinedReferral(false);
  };

  const handleDeclineReferral = () => {
    setDeclinedReferral(true);
    setReferralOpened(false);
  };

  const resetSelection = () => {
    setSelectedPath(null);
    setReferralOpened(false);
    setDeclinedReferral(false);
  };

  return (
    <div className={styles.landingRoot}>
      <div className={styles.topBanner}>
        <img src={require('./SVGs/image001.png')} alt="Banner" className={styles.bannerImg} />
        <div className={styles.bannerOverlay} />
        <div className={styles.navBar}>
          <AxisLogo variant="white" size="extra-large" className={styles.navLogo} onClick={() => window.open(AXIS_WEBSITE, '_blank')} style={{ cursor: 'pointer' }} />
          <span className={styles.navPhone}>{NAV_PHONE}</span>
          <div className={styles.navLinks}>
            {currentNavLinks.map(link => (
              <button key={link.label} className={styles.navLink} onClick={() => handleNavClick(link.action)}>
                {link.label}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.bannerContent}>
          <h1 className={styles.bannerTitle}>Book Online</h1>
          <div className={styles.bannerSubtitle}>
            Tell us a bit about the type of care you're interested in so we can guide you to the right next step.
          </div>
        </div>
      </div>

      <div className={styles.landingMain}>
        {selectedPath === null && (
          <>
            <div className={styles.landingHeader}>
              <h2 className={styles.landingTitle}>
                Are you looking for talk therapy or medication management, Spravato esketamine therapy, or TMS?
              </h2>
              <div className={styles.landingSubtitle}>
                Select the option that best fits what you are seeking today.
              </div>
            </div>

            <div className={styles.landingCards}>
              <div
                className={styles.landingCard}
                onClick={handleSelectTherapy}
                role="button"
                tabIndex={0}
                onKeyPress={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    handleSelectTherapy();
                  }
                }}
              >
                <div className={styles.cardIconCircle}>
                  <span role="img" aria-label="Phone">📞</span>
                </div>
                <div className={styles.cardTitle}>Talk Therapy</div>
                <div className={styles.cardDesc}>
                  I’m hoping to work with a therapist for counseling or psychotherapy.
                </div>
              </div>

              <div
                className={styles.landingCard}
                onClick={() => onProceed()}
                role="button"
                tabIndex={0}
                onKeyPress={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    onProceed();
                  }
                }}
              >
                <div className={styles.cardIconCircle}>
                  <span role="img" aria-label="Medication">💊</span>
                </div>
                <div className={styles.cardTitle}>Medication, Spravato, or TMS</div>
              <div className={styles.cardDesc}>
                I’m interested in medication management, Spravato esketamine therapy, or Deep TMS.
              </div>
              </div>
            </div>
          </>
        )}

        {selectedPath === 'therapy' && (
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>Talk Therapy Availability</div>
            </div>
            <div className={styles.cardBody}>
              <p>
                Our therapists currently have a 3-month waitlist. However, we have a wide network of therapists who we can refer you to. Would you like us to contact you with a referral for another therapy practice?
              </p>
            </div>

            <div className={styles.actions}>
              <button className={styles.primaryButton} onClick={handleReferralRequest}>
                Yes, please contact me
              </button>
              <button className={styles.secondaryButton} onClick={handleDeclineReferral}>
                No, thank you
              </button>
            </div>

            {referralOpened && (
              <div className={styles.helpStatement}>
                We’ve opened the referral request form in a new tab. Please complete it so our team can reach out with options.
              </div>
            )}

            {declinedReferral && (
              <div className={`${styles.helpStatement} ${styles.therapyFollowUpMessage}`}>
                Thank you for your interest with Axis Integrated Mental Health. If you ever have an interest in medication management, Spravato esketamine therapy, or Deep TMS, please don’t hesitate to book an appointment with a psychiatric provider.
              </div>
            )}

            <div className={styles.actions}>
              <button className={styles.secondaryButton} onClick={resetSelection}>
                ← Back
              </button>
              <button className={styles.cardBtnFilled} onClick={onProceed}>
                Explore medication, Spravato, or TMS
              </button>
            </div>
          </div>
        )}
      </div>

      <footer className={styles.landingFooter}>
        <div className={styles.footerContent}>
          <div className={styles.footerTitle}>{ORGANIZATION_NAME}</div>
          <div className={styles.footerRating}>
            <span className={styles.footerStars}>★★★★★</span>
            <span className={styles.footerAvg}>Average Rating: 4.9/5</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TherapyPreferenceStep;

